import * as React from 'react'
import Button from '@doordash/component-button'
import s from './strings'
import styled from 'styled-components'
import Toggle from '@doordash/component-toggle'
import { ErrorMessage } from '@doordash/component-fields'
import { useEffect, useRef, useState } from 'react'
import {
  Colors,
  Icon,
  InlineChildren,
  Inset,
  Spacing,
  StackChildren,
  Text,
} from '@doordash/design-language'
import {
  PreviousAlias,
  PreviousAliasProps,
} from '../../molecules/previous-alias/PreviousAlias'
import { FormattedDivider } from '../../../components/atoms/formatted-divider/FormattedDivider'


export const BannerWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: ${Spacing.XxxSmall}px;
  background-color: ${Colors.BannerNegativeBackground};
`

const NEW_ALIAS = {
  type: 'ALIAS',
  firstName: '',
  middleName: '',
  lastName: '',
  isSingleName: false,
  valid: false,
}

type AliasTemplateResponseData = {
  INPUT_TYPE_ALIAS_DATA: AliasTemplateResponseJSONData
}

type AliasTemplateResponseJSONData = {
  json_data: AliasHistoryJsonData
}

type AliasHistoryJsonData = {
  userHasAlias: boolean,
  alias: PreviousAliasProps[]
}

type AliasHistoryProps = {
  /**
   * function to submit all aliases on click
   * of submit button when all form errors have been fixed
   */
  onSubmitStep: (aliases: AliasTemplateResponseData) => void
}

export const AliasHistory: React.FC<AliasHistoryProps> = ({ onSubmitStep }) => {
  const refs = useRef<Array<HTMLElement>>([])
  const [error, setError] = useState<string>('')
  const [submitted, setSubmitted] = useState<boolean>(false)
  const [hasAliases, setHasAliases] = useState<boolean>(false)
  const [currentAliasId, setCurrentAliasId] = useState<number>(1)
  const [aliases, setAliases] = useState<PreviousAliasProps[]>([
    {
      id: 1,
      ...NEW_ALIAS,
    },
  ])

  const addNewAlias = () => {
    const newAlias = {
      id: currentAliasId + 1,
      ...NEW_ALIAS,
    }
    setCurrentAliasId(currentAliasId + 1)
    setAliases(aliases => [...aliases, newAlias])
  }

  const removeAlias = (aliasId: number) => {
    setSubmitted(false)
    if (aliases.length === 1) {
      return
    }
    setAliases(aliases => aliases.filter(alias => alias.id !== aliasId))
  }

  const updateAlias = (aliasId: number, data: PreviousAliasProps) => {
    setAliases(aliases => {
      const newAliases = aliases.map(alias => {
        if (aliasId === alias.id) {
          return {
            id: alias.id,
            valid: true,
            ...data,
          }
        }
        return alias
      })
      return newAliases
    })
  }

  const onSubmit = () => {
    setSubmitted(true)
    setError('')
    refs.current.forEach(
      form =>
        form && form.dispatchEvent(new Event('submit', { cancelable: true }))
    )
  }

  useEffect(() => {
    if (submitted && !hasAliases) {
      onSubmitStep({
        INPUT_TYPE_ALIAS_DATA: {
          json_data: {
            userHasAlias: false,
            alias: []
          }
        }
      })
    } else if (submitted && !aliases.some(alias => !alias.valid)) {
      onSubmitStep({
        INPUT_TYPE_ALIAS_DATA: {
          json_data: {
            userHasAlias: true,
            alias: aliases
          }
        }
      })
    }
  }, [aliases, hasAliases, onSubmitStep, submitted])

  return (
    <Inset>
      <StackChildren size={StackChildren.Sizes.XLarge}>
        <StackChildren size={StackChildren.Sizes.XxxSmall}>
          {error && (
            <BannerWrapper>
              <ErrorMessage error={error} />
            </BannerWrapper>
          )}
          <Text styles={Text.Styles.PageTitle2}>{s.knownByOtherNames()}</Text>
          <Text
            styles={Text.Styles.Body2}
            color={Colors.ButtonFlatSecondaryForegroundPressed}
          >
            {s.listAllAliases()}
          </Text>
        </StackChildren>
        <Toggle
          label={s.noAliasToggle()}
          name="no-alias"
          isSelected={!hasAliases}
          type={Toggle.Types.Radio}
          onChange={() => {
            setHasAliases(!hasAliases)
            setSubmitted(false)
          }}
        />
        <Toggle
          label={s.hasAliasToggle()}
          name="has-alias"
          isSelected={hasAliases}
          type={Toggle.Types.Radio}
          onChange={() => {
            setHasAliases(!hasAliases)
            setSubmitted(false)
          }}
        />
        <FormattedDivider />
        {hasAliases &&
          aliases.map(alias => (
            <React.Fragment key={alias.id}>
              <PreviousAlias
                updateAliases={updateAlias}
                removeAlias={removeAlias}
                alias={alias}
                refs={refs}
              />
              <FormattedDivider />
            </React.Fragment>
          ))}
        {hasAliases && (
          <InlineChildren justifyContent="flex-start">
            <Button
              leadingIcon={Icon.Types.AddCircleFill}
              onClick={addNewAlias}
              type={Button.Types.Tertiary}
              isInline={true}
            >
              {s.addAnotherAlias()}
            </Button>
          </InlineChildren>
        )}
        <StackChildren size={StackChildren.Sizes.XxxSmall}>
          <Button onClick={onSubmit} disabled={true}>
            {s.continue()}
          </Button>
        </StackChildren>
      </StackChildren>
    </Inset>
  )
}
